import { useMemo, useRef, useEffect } from "react";

/**
 * Layered living scientific environment.
 * Fixed to viewport, sits behind everything. Mouse parallax on 7 layers.
 * Content readability: every animated element sits at low opacity + heavy blur where relevant.
 */
export default function GlobalBackground() {
  const rootRef = useRef(null);

  // Particle universe (electrons/photons/dust) - one-time random layout
  const particles = useMemo(() => (
    Array.from({ length: 40 }).map((_, i) => ({
      key: `p-${i}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 1 + Math.random() * 3,
      color: ["#67E8F9", "#818CF8", "#C084FC", "#A5F3FC"][i % 4],
      duration: 14 + Math.random() * 20,
      delay: Math.random() * 8,
      opacity: 0.3 + Math.random() * 0.4,
    }))
  ), []);

  // Molecular connections - randomised nodes + edges
  const molecularNodes = useMemo(() => (
    Array.from({ length: 22 }).map((_, i) => ({
      id: `mn-${i}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 0.4 + Math.random() * 0.6,
    }))
  ), []);
  const molecularEdges = useMemo(() => {
    const list = [];
    for (let i = 0; i < molecularNodes.length; i++) {
      for (let j = i + 1; j < molecularNodes.length; j++) {
        const a = molecularNodes[i], b = molecularNodes[j];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 22) list.push({ id: `${a.id}-${b.id}`, a, b, d });
      }
    }
    return list;
  }, [molecularNodes]);

  // Neural network - separate, more sparse
  const neuralNodes = useMemo(() => (
    [ [15,20],[35,45],[55,25],[75,50],[90,20], [12,72],[38,88],[62,80],[85,72],[8,50] ]
      .map(([x, y], i) => ({ id: `nn-${i}`, x, y }))
  ), []);
  const neuralEdges = useMemo(() => {
    const list = [];
    for (let i = 0; i < neuralNodes.length; i++) {
      for (let j = i + 1; j < neuralNodes.length; j++) {
        const a = neuralNodes[i], b = neuralNodes[j];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 35) list.push({ id: `${a.id}-${b.id}`, a, b, len: d });
      }
    }
    return list;
  }, [neuralNodes]);

  // Mouse parallax - write CSS vars, no React re-render
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let rafId = 0;
    let targetX = 0, targetY = 0, currentX = 0, currentY = 0;

    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      targetX = (e.clientX / w - 0.5) * 2; // -1..1
      targetY = (e.clientY / h - 0.5) * 2;
    };

    const tick = () => {
      // ease toward target
      currentX += (targetX - currentX) * 0.06;
      currentY += (targetY - currentY) * 0.06;
      el.style.setProperty("--mx", currentX.toFixed(4));
      el.style.setProperty("--my", currentY.toFixed(4));
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => { window.removeEventListener("pointermove", onMove); cancelAnimationFrame(rafId); };
  }, []);

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 -z-50 pointer-events-none overflow-hidden"
      style={{ ["--mx"]: 0, ["--my"]: 0 }}
    >
      {/* Layer 1 — Aurora gradient (slow 55s drift) */}
      <div
        data-testid="bg-aurora"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 20% 30%, hsl(var(--primary) / 0.55), transparent 60%)," +
            "radial-gradient(ellipse 70% 50% at 80% 70%, hsl(var(--secondary) / 0.50), transparent 60%)," +
            "radial-gradient(ellipse 60% 60% at 60% 20%, hsl(var(--accent) / 0.40), transparent 60%)",
          animation: "aurora-drift 55s ease-in-out infinite alternate",
          transform: "translate3d(calc(var(--mx) * 8px), calc(var(--my) * 8px), 0)",
          willChange: "transform",
        }}
      />

      {/* Layer 6 — Scientific grid */}
      <div className="absolute inset-0 grid-bg opacity-[0.22]" />

      {/* Layer 7 — Floating glass blobs (parallax +12px) */}
      <div
        className="absolute inset-0"
        style={{ transform: "translate3d(calc(var(--mx) * 12px), calc(var(--my) * 12px), 0)", willChange: "transform" }}
      >
        <div className="absolute top-[15%] -left-32 w-[520px] h-[520px] rounded-full bg-indigo-500/25 blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-[15%] -right-32 w-[520px] h-[520px] rounded-full bg-cyan-500/25 blur-3xl animate-pulse-glow" style={{ animationDelay: "3s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full bg-purple-500/18 blur-3xl animate-pulse-glow" style={{ animationDelay: "5s" }} />
        <div className="absolute top-[5%] right-[20%] w-[260px] h-[260px] rounded-full bg-blue-400/20 blur-2xl animate-float-slow" />
        <div className="absolute bottom-[10%] left-[15%] w-[220px] h-[220px] rounded-full bg-cyan-400/20 blur-2xl animate-float-slow" style={{ animationDelay: "4s" }} />
      </div>

      {/* Layer 3 — Molecular connections (parallax +6px) */}
      <svg
        data-testid="bg-molecular"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 100 100"
        style={{ transform: "translate3d(calc(var(--mx) * 6px), calc(var(--my) * 6px), 0)", opacity: 0.45, willChange: "transform" }}
      >
        {molecularEdges.map(e => (
          <line key={e.id} x1={e.a.x} y1={e.a.y} x2={e.b.x} y2={e.b.y}
            stroke="url(#molGrad)" strokeWidth={0.22} />
        ))}
        {molecularNodes.map(n => (
          <circle key={n.id} cx={n.x} cy={n.y} r={n.size * 1.8} fill="#0891B2" />
        ))}
        <defs>
          <linearGradient id="molGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0891B2" />
            <stop offset="100%" stopColor="#4F46E5" />
          </linearGradient>
        </defs>
      </svg>

      {/* Layer 5 — Neural network with information pulses (parallax +10px) */}
      <svg
        data-testid="bg-neural"
        className="absolute inset-0 w-full h-full opacity-[0.65]"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 100 100"
        style={{ transform: "translate3d(calc(var(--mx) * 10px), calc(var(--my) * 10px), 0)", willChange: "transform" }}
      >
        {neuralEdges.map(e => (
          <line key={e.id} x1={e.a.x} y1={e.a.y} x2={e.b.x} y2={e.b.y}
            stroke="url(#neuGrad)" strokeWidth={0.25} />
        ))}
        {neuralEdges.slice(0, 8).map((e, i) => (
          <circle key={`pulse-${e.id}`} r="0.9" fill="#0EA5E9">
            <animateMotion dur={`${5 + i * 0.7}s`} repeatCount="indefinite" path={`M${e.a.x},${e.a.y} L${e.b.x},${e.b.y}`} />
            <animate attributeName="opacity" values="0;1;0" dur={`${5 + i * 0.7}s`} repeatCount="indefinite" />
          </circle>
        ))}
        {neuralNodes.map((n, i) => (
          <circle key={n.id} cx={n.x} cy={n.y} r="1.1" fill="#4F46E5">
            <animate attributeName="r" values="0.7;1.6;0.7" dur={`${3.5 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>
        ))}
        <defs>
          <linearGradient id="neuGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4F46E5" />
            <stop offset="50%" stopColor="#9333EA" />
            <stop offset="100%" stopColor="#0EA5E9" />
          </linearGradient>
        </defs>
      </svg>

      {/* Layer 4 — DNA helix (both edges, opacity ~0.45) */}
      <svg data-testid="bg-dna-right" className="absolute right-0 top-0 h-full w-20 sm:w-28 opacity-[0.45]" viewBox="0 0 40 200" preserveAspectRatio="none">
        {Array.from({ length: 18 }).map((_, i) => {
          const y = i * 11 + 5;
          const phase = i * 0.5;
          const x1 = 20 + Math.sin(phase) * 14;
          const x2 = 20 - Math.sin(phase) * 14;
          return (
            <g key={`dna-r-${i}`}>
              <line x1={x1} y1={y} x2={x2} y2={y} stroke="#0EA5E9" strokeWidth={0.42} />
              <circle cx={x1} cy={y} r={1.6} fill="#0EA5E9">
                <animate attributeName="cx" values={`${x1};${x2};${x1}`} dur="7s" begin={`${i * 0.1}s`} repeatCount="indefinite" />
              </circle>
              <circle cx={x2} cy={y} r={1.6} fill="#4F46E5">
                <animate attributeName="cx" values={`${x2};${x1};${x2}`} dur="7s" begin={`${i * 0.1}s`} repeatCount="indefinite" />
              </circle>
            </g>
          );
        })}
      </svg>
      <svg data-testid="bg-dna-left" className="absolute left-0 top-0 h-full w-20 sm:w-28 opacity-[0.45]" viewBox="0 0 40 200" preserveAspectRatio="none">
        {Array.from({ length: 18 }).map((_, i) => {
          const y = i * 11 + 5;
          const phase = i * 0.5 + Math.PI;
          const x1 = 20 + Math.sin(phase) * 14;
          const x2 = 20 - Math.sin(phase) * 14;
          return (
            <g key={`dna-l-${i}`}>
              <line x1={x1} y1={y} x2={x2} y2={y} stroke="#9333EA" strokeWidth={0.42} />
              <circle cx={x1} cy={y} r={1.6} fill="#9333EA">
                <animate attributeName="cx" values={`${x1};${x2};${x1}`} dur="9s" begin={`${i * 0.15}s`} repeatCount="indefinite" />
              </circle>
              <circle cx={x2} cy={y} r={1.6} fill="#4F46E5">
                <animate attributeName="cx" values={`${x2};${x1};${x2}`} dur="9s" begin={`${i * 0.15}s`} repeatCount="indefinite" />
              </circle>
            </g>
          );
        })}
      </svg>

      {/* Layer 2 — Particle universe (parallax +15px, biggest movement) */}
      <div
        data-testid="bg-particles"
        className="absolute inset-0"
        style={{ transform: "translate3d(calc(var(--mx) * 15px), calc(var(--my) * 15px), 0)", willChange: "transform" }}
      >
        {particles.map(p => (
          <span
            key={p.key}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`, top: `${p.y}%`,
              width: p.size * 2, height: p.size * 2,
              background: p.color, opacity: Math.min(1, p.opacity + 0.4),
              boxShadow: `0 0 ${p.size * 6}px ${p.color}`,
              animation: `particle-float ${p.duration}s ease-in-out ${p.delay}s infinite`,
              willChange: "transform, opacity",
            }}
          />
        ))}
      </div>

      {/* Keyframes local to this component */}
      <style>{`
        @keyframes aurora-drift {
          0% { transform: translate3d(0, 0, 0) rotate(0deg) scale(1); }
          50% { transform: translate3d(30px, -20px, 0) rotate(4deg) scale(1.05); }
          100% { transform: translate3d(-20px, 30px, 0) rotate(-3deg) scale(1); }
        }
        @keyframes particle-float {
          0%, 100% { transform: translate3d(0, 0, 0); opacity: 0.25; }
          50% { transform: translate3d(20px, -30px, 0); opacity: 0.75; }
        }
      `}</style>
    </div>
  );
}
